/**
 * ProfilePage — `/profile`  (superall-shell-05)
 *
 * Sections
 * ─────────
 * 1. AccountCard     — avatar initials, full name, email, phone (read-only +
 *                      "edit coming soon" CTA)
 * 2. LinkedBUsCard   — list of all live BUs with open/disconnect actions
 * 3. SecurityCard    — current session info + sign-out button
 * 4. LanguageCard    — toggle between English and Arabic
 *
 * User data comes from `useAuth()` — no extra API call needed because the
 * `PlatformUser` payload is already available in the AuthProvider.
 *
 * Accessibility: every section is a <section> with aria-label; the sign-out
 * button has an explicit aria-label; language buttons have aria-pressed states.
 */

import * as React from 'react';
import { FadeIn } from '@workspace/ui';

import { BU_SURFACES, handoffToBU, type BUSurface } from '../lib/handoff';
import { STATUS_STYLES } from '../lib/badges';
import { useAuth } from '../providers/AuthProvider';
import { useLang, useT } from '../providers/LangProvider';

// ── avatar ────────────────────────────────────────────────────────────────────

function Initials({ name }: { name: string }) {
  const parts = name.trim().split(/\s+/);
  const letters =
    parts.length >= 2 ? `${parts[0][0]}${parts[parts.length - 1][0]}` : (parts[0]?.[0] ?? '?');
  return (
    <div
      aria-hidden="true"
      className="flex h-16 w-16 items-center justify-center rounded-full bg-purple-700 text-2xl font-bold text-white"
    >
      {letters.toUpperCase()}
    </div>
  );
}

// ── section wrapper ───────────────────────────────────────────────────────────

function SectionCard({
  label,
  children,
  delay = 0,
}: {
  label: string;
  children: React.ReactNode;
  delay?: number;
}) {
  return (
    <FadeIn delay={delay}>
      <section
        aria-label={label}
        className="mb-4 rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-5 py-5 shadow-sm"
      >
        <h2 className="mb-4 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
          {label}
        </h2>
        {children}
      </section>
    </FadeIn>
  );
}

// ── linked BU row ─────────────────────────────────────────────────────────────

function LinkedBURow({ bu }: { bu: BUSurface }) {
  const t = useT();
  const [lang] = useLang();
  const { token } = useAuth();
  const [opening, setOpening] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);

  const name = lang === 'ar' ? bu.nameAr : bu.nameEn;
  const desc = lang === 'ar' ? bu.descAr : bu.descEn;

  async function open() {
    if (!token) return;
    setOpening(true);
    setErr(null);
    try {
      await handoffToBU(bu, token, import.meta.env.DEV);
    } catch (e) {
      setErr(e instanceof Error ? e.message : t.handoffError);
    } finally {
      setOpening(false);
    }
  }

  return (
    <li className="flex items-center gap-3 py-3 border-b border-neutral-100 dark:border-neutral-800 last:border-b-0">
      <div
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-neutral-100 dark:bg-neutral-800 text-xl"
        aria-hidden="true"
      >
        {bu.icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-neutral-900 dark:text-neutral-100">
          {name}
        </p>
        <p className="text-xs text-neutral-400 dark:text-neutral-500">{desc}</p>
        {err && <p className="mt-0.5 text-xs text-red-600">{err}</p>}
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[bu.status]}`}
        >
          {bu.status === 'live' ? t.live : bu.status === 'beta' ? t.betaStatus : t.soon}
        </span>
        {bu.status !== 'soon' && bu.handoffApiPath && (
          <button
            type="button"
            disabled={opening}
            onClick={() => void open()}
            className="rounded-lg border border-neutral-200 dark:border-neutral-700 px-2.5 py-1 text-xs font-semibold text-purple-700 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-950/30 disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
          >
            {opening ? t.opening : t.profilePage.openService}
          </button>
        )}
      </div>
    </li>
  );
}

// ── page ──────────────────────────────────────────────────────────────────────

export function ProfilePage(): React.ReactElement {
  const t = useT();
  const [lang, setLang] = useLang();
  const { user, logout } = useAuth();
  const [showEditModal, setShowEditModal] = React.useState(false);

  const name = user?.fullName ?? t.profilePage.namePlaceholder;
  const email = user?.email ?? t.profilePage.emailPlaceholder;

  const liveBUs = BU_SURFACES.filter((b) => b.status !== 'soon');

  return (
    <FadeIn>
      <h1 className="mb-4 text-xl font-bold text-neutral-900 dark:text-neutral-100">
        {t.profilePage.title}
      </h1>

      {/* ── 1. Account card ──────────────────────────── */}
      <SectionCard label={t.profilePage.accountSection} delay={0}>
        <div className="flex items-start gap-4 mb-4">
          <Initials name={name} />
          <div className="min-w-0">
            <p className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">{name}</p>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 truncate">{email}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowEditModal(true)}
          className="w-full rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 px-4 py-2.5 text-sm font-semibold text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
        >
          {t.profilePage.editProfile}
        </button>
      </SectionCard>

      {/* ── 2. Linked BUs ────────────────────────────── */}
      <SectionCard label={t.profilePage.linkedBUsSection} delay={0.08}>
        <p className="mb-3 text-xs text-neutral-500 dark:text-neutral-400">
          {t.profilePage.linkedBUsDetail}
        </p>
        {liveBUs.length === 0 ? (
          <p className="text-sm text-neutral-400 dark:text-neutral-500">{t.soon}</p>
        ) : (
          <ul>
            {liveBUs.map((bu) => (
              <LinkedBURow key={bu.id} bu={bu} />
            ))}
          </ul>
        )}
      </SectionCard>

      {/* ── 3. Language ──────────────────────────────── */}
      <SectionCard label={t.profilePage.languageSection} delay={0.16}>
        <div className="flex gap-2">
          <button
            type="button"
            aria-pressed={lang === 'en'}
            onClick={() => setLang('en')}
            className={`flex-1 rounded-xl border px-4 py-2.5 text-sm font-semibold transition focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 ${
              lang === 'en'
                ? 'border-purple-600 bg-purple-700 text-white'
                : 'border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800'
            }`}
          >
            {t.profilePage.languageEn}
          </button>
          <button
            type="button"
            aria-pressed={lang === 'ar'}
            onClick={() => setLang('ar')}
            className={`flex-1 rounded-xl border px-4 py-2.5 text-sm font-semibold transition focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 ${
              lang === 'ar'
                ? 'border-purple-600 bg-purple-700 text-white'
                : 'border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800'
            }`}
          >
            {t.profilePage.languageAr}
          </button>
        </div>
      </SectionCard>

      {/* ── 4. Security / session ────────────────────── */}
      <SectionCard label={t.profilePage.securitySection} delay={0.24}>
        <p className="mb-1 text-sm text-neutral-700 dark:text-neutral-300">
          {t.profilePage.signedInAs}
        </p>
        <p className="mb-4 text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
          {email}
        </p>
        <p className="mb-4 text-xs text-neutral-400 dark:text-neutral-500">
          {t.profilePage.sessionNote}
        </p>
        <button
          type="button"
          onClick={logout}
          aria-label={t.signOutAria}
          className="w-full rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 px-4 py-2.5 text-sm font-semibold text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
        >
          {t.profilePage.signOutAll}
        </button>
      </SectionCard>

      {/* ── Edit coming-soon modal ───────────────────── */}
      {showEditModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-modal-title"
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
          onClick={() => setShowEditModal(false)}
        >
          <div
            className="w-full max-w-sm rounded-t-2xl sm:rounded-2xl bg-white dark:bg-neutral-900 px-6 py-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 text-2xl" aria-hidden="true">
              ✏️
            </div>
            <h2
              id="edit-modal-title"
              className="mb-2 text-lg font-semibold text-neutral-900 dark:text-neutral-100"
            >
              {t.profilePage.editComingSoon}
            </h2>
            <p className="mb-5 text-sm text-neutral-500 dark:text-neutral-400">
              {t.profilePage.editComingSoonDetail}
            </p>
            <button
              type="button"
              onClick={() => setShowEditModal(false)}
              className="w-full rounded-xl bg-neutral-900 dark:bg-neutral-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-neutral-800 focus-visible:ring-2 focus-visible:ring-neutral-500 focus-visible:ring-offset-2"
            >
              {t.walletPage.close}
            </button>
          </div>
        </div>
      )}
    </FadeIn>
  );
}
